import { useState } from "react";
import {
  DisplayFormErrors,
  InputField,
  SharedserviceMelcoModelsAlphabetCollection,
  SharedserviceMelcoModelsAlphabetListItem,
  VerticalForm,
  Drawer as DrawerType,
  DebugForm,
} from "melco-shared-logic";
import { useTranslation } from "react-i18next";
import { QueryObserverResult } from "react-query";
import {
  Col,
  DesignTokens,
  FullWidthSpace,
  PreviewSider,
  Row,
  Title,
  Alert,
} from "melco-ui";
import { useField } from "formik";
import isEmpty from "lodash-es/isEmpty";
import { SavePublishHeader } from "../../header/SavePublishHeader";
import { FontCollectionFontsList } from "./FontCollectionFontsList";
import { FontsSortableList } from "./FontsSortableList";
import { useRevertFontCollection } from "../../../hooks/libraries//fontCollections/useRevertFontCollection";
import { useFontCollectionReferencesCount } from "../../../hooks/libraries/fontCollections/useFontCollectionReferencesCount";
import { AddFontsDrawer } from "./AddFontsDrawer";

const { spacings } = DesignTokens;

type DisplayFontCollectionDetailFormProps = {
  fontCollection: SharedserviceMelcoModelsAlphabetCollection;
  refetch: () => Promise<
    QueryObserverResult<SharedserviceMelcoModelsAlphabetCollection, unknown>
  >;
  referencesDrawer: DrawerType;
};

export const DisplayFontCollectionDetailForm: React.FC<
  DisplayFontCollectionDetailFormProps
> = ({ fontCollection, refetch, referencesDrawer }) => {
  const {
    id,
    publish_state: publishState,
    created_date: createdDate,
    updated_date: updatedDate,
    publish_date: publishDate,
    warning_list: warningList,
  } = fontCollection;

  const { t } = useTranslation();
  const [revertFontCollection, isLoading] = useRevertFontCollection(
    id!,
    refetch
  );
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  const [{ value: fonts }] =
    useField<SharedserviceMelcoModelsAlphabetListItem[]>("alphabet_list");

  const { total: referencesCount } = useFontCollectionReferencesCount(id!);

  return (
    <>
      <SavePublishHeader
        revertAction={revertFontCollection}
        isCurrentlyReverting={isLoading}
        publishedState={publishState!}
        createdDate={createdDate!}
        updatedDate={updatedDate}
        publishedDate={publishDate}
        warnings={warningList ?? undefined}
        referencesCount={referencesCount}
        referencesDrawer={referencesDrawer}
      />
      <DisplayFormErrors />
      <FullWidthSpace direction="vertical" size="middle">
        <Alert
          message={t(
            "libraries.font_collections.detail.form.embroideryWarning"
          )}
          type="info"
          withBackground
          closable={false}
        />
        <Row gutter={[spacings.xlarge, spacings.xlarge]}>
          <Col xs={isEmpty(fonts) ? 24 : 16}>
            <VerticalForm>
              <FullWidthSpace direction="vertical">
                <div>
                  <InputField
                    name="name"
                    label={t(
                      "libraries.font_collections.detail.form.label.name"
                    )}
                  />
                </div>
                <FontCollectionFontsList setDrawerOpen={setDrawerOpen} />
              </FullWidthSpace>
            </VerticalForm>
            <AddFontsDrawer
              isDrawerOpen={isDrawerOpen}
              setDrawerOpen={setDrawerOpen}
            />
          </Col>

          {!isEmpty(fonts) && (
            <Col xs={8}>
              <PreviewSider>
                <FullWidthSpace direction="vertical" size="small">
                  <Title level={5}>
                    {t("libraries.font_collections.detail.sort_sider.title")}
                  </Title>
                  <div>
                    <FontsSortableList />
                  </div>
                </FullWidthSpace>
              </PreviewSider>
            </Col>
          )}
        </Row>
      </FullWidthSpace>
      {false && <DebugForm />}
    </>
  );
};
